
import VueApp from '@/@types/app/VueApp';
import { CompanyUserStatus } from '@/@types/enums/CompanyUserStatus';
import { PERSONAL_WORKSPACE_TITLE } from '@/constants/SharedConsts';
import { Company } from '@/models/Company';
import {
  BotConfig,
  NotesAutoGeneration,
  NotesAutoGenerationLabels,
  ParagraphsAutoGeneration,
  ParagraphsAutoGenerationLabels,
  PublishingArticles,
  PublishingArticlesLabels,
  NoteTakerMode,
  NoteTakerModeLabels,
} from '@/models/forms/BotConfig';
import { Workspace } from '@/models/Workspace';
import notificationService from '@/services/NotificationService';
import store from '@/store';
import { Loading } from 'element-ui';
import { Component, Watch } from 'vue-property-decorator';
import { Meeting } from '@/@types/Meeting';

@Component({
  computed: {
    ParagraphsAutoGenerationLabels() {
      return ParagraphsAutoGenerationLabels;
    },
    NotesAutoGenerationLabels() {
      return NotesAutoGenerationLabels;
    },
    PublishingArticlesLabels() {
      return PublishingArticlesLabels;
    },
    NoteTakerModeLabels() {
      return NoteTakerModeLabels;
    },
  },
})
export default class BotSettingsDialog extends VueApp {
  visible = false;
  resolve: any = null;
  reject: any = null;

  botCompany: Company = new Company();
  botWorkspace: Pick<Workspace, 'id' | 'title'> = new Workspace();
  botNameSuffix = `'s Snaplore AI`;
  botConfig: BotConfig | null = null;
  event: Meeting | undefined = undefined;
  title: string | null = null;

  availableBotWorkspaces: Pick<Workspace, 'id' | 'title'>[] = [];

  isMeetingConfig = false;

  get isShowCustomPrompt() {
    return this.botConfig?.notesAutoGeneration === NotesAutoGeneration.CUSTOM;
  }

  get user() {
    return store.state.user;
  }

  get userActiveCompanies(): Company[] {
    const companies: Company[] = this.$store.state.userCompanies;
    const inactiveCompanies: Company[] = [];
    let activeCompanies: Company[] = [];
    companies.forEach((company: Company) => {
      const isInactiveUserInCompany = company.roles?.some(
        (item) => item.status === CompanyUserStatus.DEACTIVATED && item.user === this.user.id
      );
      if (isInactiveUserInCompany) {
        inactiveCompanies.push(company);
      }
    });

    if (inactiveCompanies.length) {
      companies.forEach((company: Company) => {
        inactiveCompanies.forEach((item: Company) => {
          if (company.id !== item.id) {
            activeCompanies.push(company);
          }
        });
      });
    } else {
      activeCompanies = companies;
    }
    return activeCompanies;
  }

  workspaceTitle = (title: string) => (title?.indexOf('PW_') == 0 ? PERSONAL_WORKSPACE_TITLE : title);

  placeholderTitle(title?: string): string {
    return this.botWorkspace.title && title ? this.workspaceTitle(title) : 'Select a workspace';
  }

  getParagraphsOptionLabel(label: string): string {
    if (label.toLowerCase() === ParagraphsAutoGeneration.Manually.toLowerCase()) return `${label} (default)`;
    return label;
  }

  getPublishingOptionLabel(label: string): string {
    if (label.toLowerCase() === PublishingArticles.Manually.toLowerCase()) return `${label} (default)`;
    return label;
  }

  getNoteTakerModeLabel(label: string): string {
    if (label === NoteTakerModeLabels[NoteTakerMode.Disabled]) return `${label} (default)`;
    return `${label} (only notes will be saved)`;
  }

  isValidName(key: 'name') {
    return this.botConfig && this.botConfig[key];
  }

  async selectBotCompany(item: number | Company) {
    const companies: Company[] = this.userActiveCompanies;
    const selectedCompany = typeof item === 'number' ? companies[item] : item;
    this.botConfig.companyId = selectedCompany?.id || null;

    // Skip workspace loading if it's not a meeting config
    if (!this.isMeetingConfig) return;

    const loader = Loading.service({ lock: true });
    try {
      if (this.botConfig.companyId) {
        await this.loadAvailableWorkspaces(this.botConfig.companyId);
      }
    } catch (error) {
      await this.$api.handleResponseError(error);
    } finally {
      loader.close();
    }
  }

  selectBotWorkspace(index: number) {
    const workspaces: Pick<Workspace, 'id' | 'title'>[] = this.availableBotWorkspaces;
    const selectedWorkspace = workspaces[index];
    this.botConfig.workspaceId = selectedWorkspace?.id;
  }

  async save() {
    const loader = Loading.service({ lock: true });

    let saved = false;

    try {
      if (!this.isValidName('name')) {
        notificationService.error('The title is required');
        return;
      }

      if (this.botConfig?.notesAutoGeneration === '') {
        this.botConfig.notesAutoGeneration = null;
      }

      if (this.event?.id) {
        this.botConfig.eventId = this.event.id;
      }

      await this.$api.createBotConfig(this.botConfig);

      saved = true;
    } catch (error) {
      await this.$api.handleResponseError(error);
    } finally {
      loader.close();
    }

    if (saved) {
      this.visible = false;
      if (!this.botConfig.workspaceId) {
        this.botConfig.workspaceId = this.botWorkspace.id;
      }
      this.resolve({ config: this.botConfig, workspaces: this.availableBotWorkspaces });
    }
  }

  async loadBotConfig(eventId?: string) {
    try {
      const res = await this.$api.getBotConfig(this.user.id, eventId);
      this.botConfig = res.data.config;

      // Set default noteTakerMode if not present
      if (!this.botConfig.noteTakerMode) {
        this.botConfig.noteTakerMode = NoteTakerMode.Disabled;
      }

      this.botCompany = this.userActiveCompanies.find((item) => item.id === this.botConfig.companyId);
    } catch (res) {
      await this.$api.handleResponseError(res);
    }
  }

  async loadAvailableWorkspaces(companyId: string): Promise<void> {
    const loader = Loading.service({ lock: true });

    try {
      const res = await this.$api.company.getWorkspaces(companyId);

      this.availableBotWorkspaces = res.data.workspaces;
      const botWorkspace = this.availableBotWorkspaces.find((item) => item.id === this.botConfig.workspaceId);
      const matchedWorkspace = botWorkspace ? botWorkspace : this.availableBotWorkspaces[0];

      if (matchedWorkspace) this.botWorkspace = matchedWorkspace;
    } catch (error) {
      this.$api.handleResponseError(error);
    } finally {
      loader.close();
    }
  }

  async show(item?: Meeting) {
    const loader = Loading.service({ lock: true });
    this.event = item;

    if (!this.event?.id) {
      this.title = 'Snaplore AI Settings';
      this.isMeetingConfig = false;
    } else {
      this.title = 'Meeting Settings';
      this.isMeetingConfig = true;
    }
    const eventId = item ? item.id : undefined;

    try {
      await this.loadBotConfig(eventId);

      if (this.isMeetingConfig) {
        await this.loadAvailableWorkspaces(this.botConfig.companyId);
      }

      this.visible = true;

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    } finally {
      loader.close();
    }
  }

  close() {
    this.resolve();
    this.visible = false;
  }

  @Watch('botConfig.noteTakerMode')
  onNoteTakerModeChange(value: NoteTakerMode) {
    if (value === NoteTakerMode.Enabled) {
      this.botConfig.notesAutoGeneration = NotesAutoGeneration.MEETINGS_NOTES;
      this.botConfig.paragraphsAutoGeneration = ParagraphsAutoGeneration.Manually;
    }
  }
}
